import React from 'react';
import {get} from 'lodash';
import {gql} from '@apollo/client';

import client from '../../../src/graphql/client';

import ArticlePage from '../../../src/containers/Community/Article';

import refactorBreadcrumbsData from '../../../src/data-restructure/navigation/breadcrumbs/breadcrumbs';
import refactorHb from '../../../src//data-restructure/multimedia/hero-banner-small-v2-2023';
import respImgQuery from '../../../src/graphql/queries/resp-img';
import refactorRespImg from '../../../src/data-restructure/ui/resp-img';
import seoQuery from '../../../src/graphql/queries/seo';
  
const Article = (props) => {
  const countryCode = process.env.DEV_COUNTRY_CODE ? process.env.DEV_COUNTRY_CODE.toLowerCase() : process.env.COUNTRY_CODE.toLowerCase();
  const siteUrl = countryCode === 'au' ? 'https://jayco.com.au' : 'https://jayco.co.nz';
  const authorUrl = (props.ambassador && props.ambassador.uri) ? (siteUrl + '/ambassadors/' + props.ambassador.uri.replace('-ccau', '').replace('-ccnz', '')) : siteUrl;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": props.title || '',
    "image": props.thumbImg ? [props.thumbImg?.original] : [],
    "datePublished": props.datePublished ? (props.datePublished + '+00:00') : '',
    "dateModified": props.dateModified ? (props.dateModified + '+00:00') : '',
    "author": [{
      "@type": "Organization",
      "name": (props.ambassador && props.ambassador.name) || 'Jayco',
      "url": authorUrl
    }]
  } 

  return (
    <>
      {process.env.DEALER_SITE !== true &&
        <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(schemaData)}} />
      }
      <ArticlePage
        {...props}
      />
    </>
  )
}

export async function getStaticPaths() {
  // For slow backends, use fallback: 'blocking' to generate pages on-demand
  // This prevents build timeouts while still providing static generation benefits
  
  if (process.env.SKIP_STATIC_GENERATION === 'true') {
    // Skip heavy path generation during development or when backend is slow
    return { paths: [], fallback: 'blocking' }
  }

  try {
    const GET_DYNAMIC_ARTICLES_QUERY = gql`
      query GET_ARTICLES_PATHS {
        articles(first: 1000) {
          nodes {
            uri
            acf_domains {
              assignedDomains
            }
            acf_article {
              artPressRoom
            }
          }
        }
      }
    `

    const result = await client.query({
      query: GET_DYNAMIC_ARTICLES_QUERY,
      // Add timeout and error handling for slow queries
      fetchPolicy: 'network-only',
      context: {
        timeout: 45000, // 45 second timeout
      }
    });

    let paths = [];
    
    const articles = get(result, 'data.articles.nodes', []);
    const domain = process.env.ASSIGNED_DOMAIN;

    articles.forEach(item => {
      const uri = get(item, 'uri', null);
      const pressRead = get(item, 'acf_article.artPressRoom', null);
      const assignedDomains = get(item, 'acf_domains.assignedDomains', []);
      if (pressRead === null && (assignedDomains.indexOf(domain) !== -1) || process.env.PREVIEW_MODE) {
        paths.push({
          params: {
            id: uri.replace('-ccau', '').replace('-ccnz', '').replace(`-au-dealers`, '').replace(`-nz-dealers`, ''),
          }
        });
      }
    });

    return { paths, fallback: 'blocking'}
  } catch (error) {
    console.warn('Static path generation failed, using fallback mode:', error.message);
    // Fallback to on-demand generation if pre-generation fails
    return { paths: [], fallback: 'blocking' }
  }
}

export async function getStaticProps({ params }) {

  const countryCode = process.env.DEV_COUNTRY_CODE ? process.env.DEV_COUNTRY_CODE.toLowerCase() : process.env.COUNTRY_CODE.toLowerCase();
  const domain = process.env.DEV_ASSIGNED_DOMAIN ? process.env.DEV_ASSIGNED_DOMAIN : process.env.ASSIGNED_DOMAIN.toLowerCase()
  const region = countryCode === 'au' ? 'jayco.com.au' : 'jayco.co.nz'

  // Merged query combining all data needs in a single request
  const GET_ALL_ARTICLE_DATA = gql`
    query GET_ALL_ARTICLE_DATA($uri: String!, $communityUri: String!) {
      # Get the specific article by URI
      articleBy(uri: $uri) {
        seo {
          ${seoQuery}
        }
        uri
        acf_domains {
          assignedDomains
        }
        title
        date
        dateGmt
        modifiedGmt
        articleId
        articleCategories {
          nodes {
            articles(first: 1000) {
              nodes {
                uri
                title
                articleId
                articleCategories {
                  nodes {
                    name
                  }
                }
                acf_article {
                  artTeaserCopy
                  artThumbnailImage {
                    ${respImgQuery}
                  }
                }
                acf_domains {
                  assignedDomains
                }
              }
            }
            name
          }
        }
        acf_article {
          artDatePosted
          artThumbnailImage {
            ${respImgQuery}
          }
          artRelatedToAmbassador {
            ... on Ambassador {
              id
              title
              uri
              acf_ambassador {
                amPhoto {
                  id
                  sourceUrl(size: THUMBNAIL)
                }
              }
            }
          }
          cbArticleContent {
            ... on Article_AcfArticle_CbArticleContent_FcArticleText {
              tArticleText
              fieldGroupName
              tArticleCtasRep {
                tArticleCtasRepLink {
                  url
                  title
                  target
                }
              }
            }
            ... on Article_AcfArticle_CbArticleContent_FcArticleImage {
              fieldGroupName
              iArticleImage {
                ${respImgQuery}
              }
            }
            ... on Article_AcfArticle_CbArticleContent_FcArticleVideo {
              fieldGroupName
              vArticleVideoUrl {
                url
              }
            }
            ... on Article_AcfArticle_CbArticleContent_FcArticleImageGallery {
              fieldGroupName
              igImagesRep {
                igImagesRepImage {
                  ${respImgQuery}
                }
              }
            }
          }
        }
      }
      
      # Get community page data for hero banner
      pageBy(uri: $communityUri) {
        uri
        acf_content_builder {
          contentBuilder {
            ... on Page_AcfContentBuilder_ContentBuilder_FcHeroBannerV22023 {
              fieldGroupName
              hb2Banners {
                hb2Title
                hb2Subtext
                hb2Image {
                  ${respImgQuery}
                }
                hb2VideoDesktop {
                  guid
                }
                hb2VideoMobile {
                  guid
                }
                hb2Link {
                  title
                  url
                  target
                }
              }
            }
          }
        }
      }
      
      # Get article categories
      articleCategories {
        nodes {
          name
          slug
          articles(first: 1) {
            edges {
              node {
                id
              }
            }
          }
        }
      }
      
      # Get all articles for featured articles
      articles(first: 1000) {
        nodes {
          uri
          title
          date
          dateGmt
          modifiedGmt
          articleId
          articleCategories {
            nodes {
              name
            }
          }
          acf_article {
            artFeaturedArticle
            artDatePosted
            artTeaserCopy
            artThumbnailImage {
              ${respImgQuery}
            }
          }
          acf_domains {
            assignedDomains
          }
        }
      }
    }
  `

  // Single query with all required data - add timeout and error handling
  let allData;
  try {
    allData = await client.query({
      query: GET_ALL_ARTICLE_DATA,
      variables: {
        uri: `${params.id}?${countryCode}`,
        communityUri: `community?${countryCode}`
      },
      fetchPolicy: 'network-only',
      context: {
        timeout: 45000, // 45 second timeout
      }
    });
  } catch (error) {
    console.error('Failed to fetch article data:', error.message);
    // Return minimal props to prevent build failure
    return {
      props: {
        title: 'Article Not Available',
        meta: { title: 'Article Not Available' },
        error: true
      },
      // Revalidate more frequently when there's an error
      revalidate: (process.env.NEXT_STATIC_EXPORT && process.env.NEXT_STATIC_EXPORT.toString() === 'true') ? false : 1,
    }
  }

  const res = get(allData, 'data.articleBy', null);

  if (res) {
    const context = get(allData, 'data.pageBy', null);

    const seo = get(allData, 'data.articleBy.seo', null);
    const hbData = get(context, 'acf_content_builder.contentBuilder', null);
    const heroBanner = hbData && hbData[0] ? refactorHb(hbData[0]) : null;
    
    const categoryNodes = get(allData, 'data.articleCategories.nodes', null);

    const categories = categoryNodes ? categoryNodes.filter(item => {
      return get(item, 'articles.edges', []).length > 0 
    }) : null;

    const title = get(allData, 'data.articleBy.title', null);
    const date = get(allData, 'data.articleBy.date', null);
    const articleID = get(allData, 'data.articleBy.articleId', null);
    const category = get(allData, 'data.articleBy.articleCategories.nodes[0].name', null);
    const contentArticleData = get(allData, 'data.articleBy.acf_article.cbArticleContent', null);

    const categoryArticles = get(allData, 'data.articleBy.articleCategories.nodes[0].articles.nodes', []);

    const assignedDomains = get(allData, 'data.articleBy.acf_domains.assignedDomains', []);

    const ambassador = {
      name: get(allData, 'data.articleBy.acf_article.artRelatedToAmbassador[0].title', null),
      image: get(allData, 'data.articleBy.acf_article.artRelatedToAmbassador[0].acf_ambassador.amPhoto', null),
      uri: get(allData, 'data.articleBy.acf_article.artRelatedToAmbassador[0].uri', null)
    };

    const articles = get(allData, 'data.articles.nodes', null);
    const articleItems = articles.filter(article => article.articleCategories && article.articleCategories.nodes.length > 0);
    const filteredArticles = articleItems.filter(article => article.acf_domains.assignedDomains.indexOf(region) !== -1);

    const articleAllConversion = filteredArticles.filter(item => {
      return get(item, 'acf_domains.assignedDomains', []).indexOf(region) !== -1
    }).map(item => {

      const title = item.title;
      const teaser = item.acf_article.artTeaserCopy;
      const uri = item.uri;
      const articleID = item.articleId;
      const featured = item.acf_article.artFeaturedArticle;
      const category = item.articleCategories.nodes[0].name;
      const img = item.acf_article.artThumbnailImage ? refactorRespImg(item.acf_article.artThumbnailImage, [{
        size: 'default',
        key: 'image_size_466x314'
      }]) : null;
  
      return {
        title,
        teaser,
        uri: uri.replace('-ccau', '').replace('-ccnz', '').replace(`-au-dealers`, '').replace(`-nz-dealers`, ''),
        articleID,
        featured,
        category,
        img,
      }
    })
    const featuredArticles = articleAllConversion ? articleAllConversion.filter(article => article.featured && article.featured.length > 0 && article.articleID !== articleID) : null;

    const articleConversion = contentArticleData ? contentArticleData.map(item => {

      const article_acf = 'Article_AcfArticle_CbArticleContent_FcArticle';

      if (item.fieldGroupName === `${article_acf}Image`) {
        const image = item.iArticleImage ? refactorRespImg(item.iArticleImage, [{
          size: 'default',
          key: 'image_size_1200x674'
        }]) : null;
        return {image}
      } else if (item.fieldGroupName === `${article_acf}Text`) {
        const text = item.tArticleText;
        const cta = item.tArticleCtasRep ? item.tArticleCtasRep.map((item) => ({
          link: get(item, 'tArticleCtasRepLink.url', null),
          target: get(item ,'tArticleCtasRepLink.target', null),
          title: get(item, 'tArticleCtasRepLink.title', null)
        })).filter(item => {
          return item.link
        }) : null;
        return {text, cta}
      } else if (item.fieldGroupName === `${article_acf}Video`) {
        const video = get(item, 'vArticleVideoUrl.url', null);
        return {video}
      } else if (item.fieldGroupName === `${article_acf}ImageGallery`) {
        const gallery = item.igImagesRep.map(item => {
          const image = item.igImagesRepImage ? refactorRespImg(item.igImagesRepImage, [{
            size: 'default',
            key: 'image_size_928x552'
          }]) : null;
          return{image}
        })
        return {gallery}
      } else {
        return {
          article_acf
        }
      }
    }) : null

    var categoryConversion = categoryArticles.filter(item => {
      const articleAssignedDomains = get(item, 'acf_domains.assignedDomains', []);
      return articleAssignedDomains.indexOf(domain) !== -1;
    }).map(item => {

      const title = item.title;
      const teaser = item.acf_article.artTeaserCopy;
      const articleID = item.articleId;
      const uri = item.uri ? item.uri.replace('-ccau', '').replace('-ccnz', '').replace(`-au-dealers`, '').replace(`-nz-dealers`, '') : null;
      const category = item.articleCategories.nodes[0].name;
      const img = item.acf_article.artThumbnailImage ? refactorRespImg(item.acf_article.artThumbnailImage, [{
        size: 'default',
        key: 'image_size_466x314'
      }]) : null;
  
      return {
        title,
        teaser,
        articleID,
        uri,
        category,
        img
      }
    });

    categoryConversion = categoryConversion.filter(function(e) { return e.articleID !== articleID });

    // Get article thumbnail for schema markup image data
    const thumbImg = get(allData, 'data.articleBy.acf_article.artThumbnailImage', null) ? refactorRespImg(allData.data.articleBy.acf_article.artThumbnailImage, [{
      size: 'default',
      key: 'image_size_466x314'
    }]) : null;

    // breadcrumbs
    const articleUrl = get(allData, 'data.articleBy.uri', false) ? '/community/article/' + allData.data.articleBy.uri.replace('-ccau', '').replace('-ccnz', '').replace(`-au-dealers`, '').replace(`-nz-dealers`, '') : false;
    const breadcrumbs = articleUrl ? await refactorBreadcrumbsData(articleUrl, assignedDomains) : null;
    
    const data = {
      meta: {
        title,
        seo,
        assignedDomains
      },
      breadcrumbs,
      heroBanner,
      categories,
      title,
      date,
      thumbImg,
      datePublished: get(allData, 'data.articleBy.dateGmt', null),
      dateModified: get(allData, 'data.articleBy.modifiedGmt', null),
      articleID,
      category,
      contentArticleData,
      categoryConversion: categoryConversion.length > 6 ? categoryConversion.slice(0, 6) : categoryConversion,
      articleConversion,
      ambassador,
      featuredArticles
    }

    return {
      revalidate: (process.env.NEXT_STATIC_EXPORT && process.env.NEXT_STATIC_EXPORT.toString() === 'true') ? false : 1,
      props: {
        ...data,
      }
    }
  } else { 
    return { 
      revalidate: (process.env.NEXT_STATIC_EXPORT && process.env.NEXT_STATIC_EXPORT.toString() === 'true') ? false : 1,
      props: {} 
    }
  }
}

export default Article
